/**
 * Utility functions for table pagination and column sorting.
 */

/**
 * Returns the slice of items for the given 1-indexed page.
 * Page 1 returns items[0..pageSize-1].
 * Returns an empty array if page is out of range or inputs are invalid.
 */
export function paginateItems<T>(items: T[], page: number, pageSize: number): T[] {
  if (!items || items.length === 0) {
    return [];
  }
  if (page < 1 || pageSize < 1) {
    return [];
  }

  const startIndex = (page - 1) * pageSize;
  if (startIndex >= items.length) {
    return [];
  }

  return items.slice(startIndex, startIndex + pageSize);
}

/**
 * Returns the total number of pages given the total item count and page size.
 * Always returns at least 1.
 */
export function getTotalPages(totalItems: number, pageSize: number): number {
  if (pageSize < 1 || totalItems < 0) {
    return 1;
  }
  if (totalItems === 0) {
    return 1;
  }
  return Math.ceil(totalItems / pageSize);
}

/**
 * Sorts a copy of items by the given column in ascending or descending order.
 * Handles string comparison (localeCompare), number comparison, and date string
 * comparison (YYYY-MM-DD lexicographic). Null/undefined values sort last regardless
 * of sort direction.
 */
export function sortItems<T>(
  items: T[],
  column: keyof T,
  direction: 'ascending' | 'descending'
): T[] {
  if (!items || items.length === 0) {
    return [];
  }

  const sorted = [...items];

  sorted.sort((a, b) => {
    const aVal = a[column];
    const bVal = b[column];

    // Null/undefined values sort last regardless of direction
    const aIsEmpty = aVal === null || aVal === undefined || aVal === '';
    const bIsEmpty = bVal === null || bVal === undefined || bVal === '';

    if (aIsEmpty && bIsEmpty) return 0;
    if (aIsEmpty) return 1;
    if (bIsEmpty) return -1;

    let comparison = 0;

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      comparison = aVal - bVal;
    } else if (typeof aVal === 'string' && typeof bVal === 'string') {
      comparison = aVal.localeCompare(bVal);
    } else {
      // Fallback: convert to string and compare
      comparison = String(aVal).localeCompare(String(bVal));
    }

    return direction === 'ascending' ? comparison : -comparison;
  });

  return sorted;
}
